export function normalizeTeamReceipts(receipts = []) {
  return (receipts || []).map((r) => ({
    userId: r?.userId,
    userName: String(r?.userName || "").trim(),
    deliveredAt: r?.deliveredAt || null,
    readAt: r?.readAt || null,
    playedAt: r?.playedAt || null,
  }));
}

/**
 * Calcula nível de status (estilo WhatsApp) para mensagens enviadas pelo usuário atual.
 * @returns {{ level: string, icon: 'sent'|'delivered'|'read', readNames: string[], playedNames: string[], deliveredCount: number, readCount: number, playedCount: number, total: number }}
 */
export function buildTeamMessageStatus({ receipts = [], messageType, spaceType }) {
  const others = normalizeTeamReceipts(receipts);
  const total = others.length;
  const isAudio = messageType === "audio";

  const base = {
    level: "sent",
    icon: "sent",
    readNames: [],
    playedNames: [],
    deliveredCount: 0,
    readCount: 0,
    playedCount: 0,
    total,
  };

  if (!total) return base;

  const delivered = others.filter((r) => r.deliveredAt || r.readAt || r.playedAt);
  const read = others.filter((r) => r.readAt);
  const played = others.filter((r) => r.playedAt);

  base.deliveredCount = delivered.length;
  base.readCount = read.length;
  base.playedCount = played.length;
  base.readNames = read.map((r) => r.userName || `#${r.userId}`).filter(Boolean);
  base.playedNames = played.map((r) => r.userName || `#${r.userId}`).filter(Boolean);

  if (spaceType === "dm") {
    const peer = others[0];
    if (isAudio && peer?.playedAt) {
      return { ...base, level: "played", icon: "read" };
    }
    if (peer?.readAt) {
      return { ...base, level: "read", icon: "read", readCount: 1 };
    }
    if (peer?.deliveredAt) {
      return { ...base, level: "delivered", icon: "delivered", deliveredCount: 1 };
    }
    return base;
  }

  if (isAudio && played.length === total) {
    return { ...base, level: "played_all", icon: "read" };
  }
  if (isAudio && played.length > 0) {
    return { ...base, level: "played_some", icon: "read" };
  }
  if (read.length === total) {
    return { ...base, level: "read_all", icon: "read" };
  }
  if (read.length > 0) {
    return { ...base, level: "read_some", icon: "read" };
  }
  if (delivered.length === total) {
    return { ...base, level: "delivered_all", icon: "delivered" };
  }
  if (delivered.length > 0) {
    return { ...base, level: "delivered_some", icon: "delivered" };
  }

  return base;
}

function receiptDisplayName(receipt) {
  return receipt.userName || `#${receipt.userId}`;
}

/**
 * Agrupa recibos em listas para o diálogo de status (estilo WhatsApp).
 * @returns {{ isAudio: boolean, total: number, read: object[], played: object[], delivered: object[], pending: object[] }}
 */
export function buildTeamMessageReceiptLists({ receipts = [], messageType }) {
  const others = normalizeTeamReceipts(receipts);
  const isAudio = messageType === "audio";

  const mapEntry = (receipt, at) => ({
    userId: receipt.userId,
    displayName: receiptDisplayName(receipt),
    at,
  });

  const read = [];
  const played = [];
  const delivered = [];
  const pending = [];

  for (const receipt of others) {
    const hasDelivered = Boolean(receipt.deliveredAt || receipt.readAt || receipt.playedAt);

    if (isAudio && receipt.playedAt) {
      played.push(mapEntry(receipt, receipt.playedAt));
    } else if (receipt.readAt) {
      read.push(mapEntry(receipt, receipt.readAt));
    } else if (hasDelivered) {
      delivered.push(mapEntry(receipt, receipt.deliveredAt));
    } else {
      pending.push(mapEntry(receipt, null));
    }
  }

  const byName = (a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" });
  read.sort(byName);
  played.sort(byName);
  delivered.sort(byName);
  pending.sort(byName);

  return { isAudio, total: others.length, read, played, delivered, pending };
}
